/**
 * Team — плоский список всей партнёрской сети (10 линий).
 *
 * Альтернатива визуализации дерева (/network/tree): реф-дерево
 * лагает на больших сетях, поэтому лидеру даём обычный список
 * с фильтрами, сортировкой и графиком дохода.
 *
 * Сортировка:
 *   1. team_turnover (оборот команды партнёра) DESC
 *   2. total_staked (сумма активных слотов) DESC
 *   3. joinedAt ASC (старые выше при равенстве)
 *
 * Фильтры:
 *   - activeOnly — только партнёры с активными слотами
 *   - byLine — опциональный фильтр по конкретной линии
 *
 * Данные: GET /api/luna/referrals/team.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Activity,
  TrendingUp,
  ArrowUpDown,
  ArrowLeft,
  Filter,
  ChevronDown,
  Crown,
  Lock,
} from 'lucide-react';
import { GlassSurface } from '@/components/glass';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useLunaProgress } from '@/hooks/useLunaProgress';
import {
  ProgressBars,
  loadProgressExpanded,
  saveProgressExpanded,
} from '@/components/nodes/ProgressBars';

// ────────────────────────────────────────────────────────────────────
// Wire types
// ────────────────────────────────────────────────────────────────────
interface TeamPartner {
  id: number;
  telegramId: string | null;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  joinedAt: string;
  lastSeenAt: string;
  line: number;
  activeSlots: number;
  totalStaked: string;
  totalContractsEver: number;
  pcActive: number;
  totalEarnedEver: string;
  incomeFromUser: string;
  teamSize: number;
  teamTurnover: string;
  currentRank: number | null;
  highestStatus: number | null;
  quickStartOnly: boolean;
  isActive: boolean;
}

interface ByLine {
  line: number;
  partners: number;
  actives: number;
  totalStaked: string;
  income: string;
}

interface DailyRow {
  day: string;
  source: string;
  total: string;
}

interface SummaryResponse {
  success: boolean;
  totals: {
    teamSize: number;
    activesCount: number;
    totalStaked: string;
    totalIncome: string;
  };
  /** Самая глубокая открытая линия — всё, что выше, считается закрытым
   *  (на фронте рисуется замочком, оборот всё равно показываем). */
  highestOpenLine?: number;
  byLine: ByLine[];
  daily: DailyRow[];
}

interface PartnersResponse {
  success: boolean;
  partners: TeamPartner[];
  pagination: {
    limit: number;
    offset: number;
    filteredTotal: number;
    hasMore: boolean;
  };
}

// Какие серии рисуем в графике — переключаются тогглами
const INCOME_SERIES = [
  { key: 'refBonus', labelKey: 'team.seriesOneTime', color: '#7C5CFF',
    sources: ['referral_bonus'] },
  { key: 'refIncome', labelKey: 'team.seriesDaily', color: '#A78BFA',
    sources: ['referral_income'] },
  { key: 'claim', labelKey: 'team.seriesClaim', color: '#10B981',
    sources: ['claim', 'auto_claim', 'plasma_core_claim'] },
  { key: 'status', labelKey: 'team.seriesStatus', color: '#F59E0B',
    sources: ['status_bonus', 'rank_bonus'] },
  { key: 'p2p', labelKey: 'team.seriesP2P', color: '#6ED3CF',
    sources: ['p2p_in'] },
  { key: 'qs', labelKey: 'team.seriesQS', color: '#EC4899',
    sources: ['quick_start_bonus', 'onboarding_bonus'] },
] as const;
type SeriesKey = typeof INCOME_SERIES[number]['key'];

const ALL_SERIES_ON: Record<SeriesKey, boolean> = {
  refBonus: true, refIncome: true, claim: true,
  status: true, p2p: true, qs: true,
};

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
function fmtUSDT(v: string | number, lang: 'ru' | 'en' = 'ru'): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  const short = parseFloat(n.toFixed(3));
  return short.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', { maximumFractionDigits: 3 });
}

function displayName(p: TeamPartner): string {
  if (p.username) return `@${p.username}`;
  if (p.firstName) return p.firstName;
  return `user#${p.id}`;
}

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;

export function Team() {
  const { t, i18n } = useTranslation();
  const lang: 'ru' | 'en' = i18n.language === 'ru' ? 'ru' : 'en';
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();

  // Summary (totals + byLine + daily) — грузится один раз
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Partners — постраничная загрузка
  const [partners, setPartners] = useState<TeamPartner[]>([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Фильтры
  const [activeOnly, setActiveOnly] = useState(false);
  const [lineFilter, setLineFilter] = useState<number | null>(null);

  // 3-барный виджет прогресса (Личный / Сеть / L1-30д) — SSE реактивно.
  const { progress, loading: progressLoading } = useLunaProgress();
  const [progressExpanded, setProgressExpanded] = useState<boolean>(() =>
    loadProgressExpanded(),
  );
  const toggleProgress = () => {
    setProgressExpanded((v) => {
      const next = !v;
      saveProgressExpanded(next);
      return next;
    });
  };
  const [sortBy, setSortBy] =
    useState<'turnover' | 'slots' | 'income'>('turnover');

  // Серии графика дохода
  const [series, setSeries] = useState<Record<SeriesKey, boolean>>(ALL_SERIES_ON);

  // 1. Summary — однократно при монтировании
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await lunaApi.referrals.teamSummary();
        if (cancelled) return;
        setSummary(res.data as SummaryResponse);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : t('team.loadError');
        setError(msg);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2. Partners — при смене фильтра/сорта сбрасываем и грузим первую страницу
  useEffect(() => {
    let cancelled = false;
    setPartnersLoading(true);
    (async () => {
      try {
        const res = await lunaApi.referrals.teamPartners({
          limit: PAGE_SIZE,
          offset: 0,
          sortBy,
          activeOnly,
          line: lineFilter,
        });
        if (cancelled) return;
        const d = res.data as PartnersResponse;
        setPartners(d.partners);
        setFilteredTotal(d.pagination.filteredTotal);
        setHasMore(d.pagination.hasMore);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : t('team.loadError');
        setError(msg);
      } finally {
        if (!cancelled) setPartnersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sortBy, activeOnly, lineFilter]);

  // Подгрузка «Показать ещё»
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    haptic.light();
    setLoadingMore(true);
    try {
      const res = await lunaApi.referrals.teamPartners({
        limit: PAGE_SIZE,
        offset: partners.length,
        sortBy,
        activeOnly,
        line: lineFilter,
      });
      const d = res.data as PartnersResponse;
      setPartners((prev) => [...prev, ...d.partners]);
      setHasMore(d.pagination.hasMore);
    } catch {
      /* silent */
    } finally {
      setLoadingMore(false);
    }
  };

  // Дневной агрегат по включённым сериям — для графика
  const chart = useMemo(() => {
    const data = summary;
    if (!data) return { days: [] as string[], values: [] as number[], max: 0 };
    // last 30 days (включая сегодня) в МСК
    const days: string[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86_400_000);
      const iso = d.toISOString().slice(0, 10);
      days.push(iso);
    }
    const activeSources = new Set<string>();
    for (const s of INCOME_SERIES) {
      if (series[s.key]) for (const src of s.sources) activeSources.add(src);
    }
    const daily: Record<string, number> = {};
    for (const d of days) daily[d] = 0;
    for (const row of data.daily) {
      if (!activeSources.has(row.source)) continue;
      if (daily[row.day] !== undefined) daily[row.day]! += Number(row.total);
    }
    const values = days.map((d) => daily[d] ?? 0);
    const max = Math.max(1e-9, ...values);
    return { days, values, max };
  }, [summary, series]);

  // Сегодняшний суммарный доход (по включённым сериям) — для header'а
  const todayIncome = chart.values[chart.values.length - 1] ?? 0;
  const total30d = chart.values.reduce((s, v) => s + v, 0);

  if (summaryLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-indigo-400" />
      </div>
    );
  }
  if (error || !summary) {
    return (
      <div className="p-4">
        <GlassSurface className="p-6 text-center">
          <p className="text-sm text-red-400">{error ?? t('team.errorTitle')}</p>
        </GlassSurface>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* 3 ШКАЛЫ — Личный / Сеть / L1-30д (SSE реактивно) */}
      <div className="px-4 pt-4">
        <ProgressBars
          progress={progress}
          loading={progressLoading}
          expanded={progressExpanded}
          onToggle={toggleProgress}
        />
      </div>

      {/* HERO — totals */}
      <div className="px-4 pt-4">
        <GlassSurface className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                haptic.light();
                navigate('/network');
              }}
              className="h-7 w-7 -ml-1 rounded-lg flex items-center
                         justify-center transition active:scale-90"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--glass-stroke)',
              }}
              aria-label={t('team.backAria')}
            >
              <ArrowLeft size={14} strokeWidth={2}
                         className="text-[var(--text-secondary)]" />
            </button>
            <Users size={16} strokeWidth={1.8}
                   className="text-[var(--accent-primary)]" />
            <h1 className="text-[15px] font-bold text-[var(--text-primary)]
                           tracking-tight">
              {t('team.title')}
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label={t('team.statAll')} value={String(summary.totals.teamSize)}
                  hint={t('team.statAllHint')} />
            <Stat label={t('team.statActive')} value={String(summary.totals.activesCount)}
                  hint={t('team.statActiveHint')} tone="positive" />
            <Stat label={t('team.statTurnover')} value={fmtUSDT(summary.totals.totalStaked, lang)}
                  hint={t('team.statTurnoverHint')} />
            <Stat label={t('team.statMyIncome')} value={fmtUSDT(summary.totals.totalIncome, lang)}
                  hint={t('team.statMyIncomeHint')} tone="positive" />
          </div>
        </GlassSurface>
      </div>

      {/* CHART */}
      <div className="px-4 pt-3">
        <GlassSurface className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} strokeWidth={1.8}
                          className="text-[var(--accent-secondary)]" />
              <span className="text-[12px] font-bold uppercase
                               tracking-[0.14em] text-[var(--text-secondary)]">
                {t('team.chartTitle')}
              </span>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-wider
                              text-[var(--text-muted)]">{t('team.chartToday')}</div>
              <div className="text-[13px] font-bold text-[var(--text-primary)]">
                +{fmtUSDT(todayIncome, lang)} USD₮
              </div>
            </div>
          </div>

          {/* Тогглы серий */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {INCOME_SERIES.map((s) => {
              const on = series[s.key];
              return (
                <button key={s.key}
                  onClick={() => {
                    haptic.light();
                    setSeries((prev) => ({ ...prev, [s.key]: !prev[s.key] }));
                  }}
                  className="inline-flex items-center gap-1 rounded-full
                             px-2 py-1 text-[10px] font-bold uppercase
                             tracking-[0.08em] transition active:scale-95"
                  style={{
                    background: on
                      ? `color-mix(in oklab, ${s.color} 18%, transparent)`
                      : 'rgba(255,255,255,0.03)',
                    color: on ? s.color : 'var(--text-muted)',
                    border: `1px solid ${on
                      ? `color-mix(in oklab, ${s.color} 35%, transparent)`
                      : 'var(--glass-stroke)'}`,
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: on ? s.color : 'var(--text-disabled)' }}
                  />
                  {t(s.labelKey)}
                </button>
              );
            })}
          </div>

          {/* Bars */}
          <SparkBars days={chart.days} values={chart.values} max={chart.max} />

          <div className="mt-3 flex items-center justify-between
                          text-[10px] text-[var(--text-muted)]">
            <span>{t('team.chart30ago')}</span>
            <span>{t('team.chartTotal', { amount: fmtUSDT(total30d, lang) })}</span>
            <span>{t('team.chartToday')}</span>
          </div>
        </GlassSurface>
      </div>

      {/* ПО ЛИНИЯМ */}
      <div className="px-4 pt-3">
        <GlassSurface className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[12px] font-bold uppercase
                             tracking-[0.14em] text-[var(--text-secondary)]">
              {t('team.byLines')}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <LineChip
              active={lineFilter === null}
              onClick={() => { haptic.light(); setLineFilter(null); }}
              label={t('team.allLines')}
              count={summary.totals.teamSize}
            />
            {summary.byLine.map((b) => {
              const hOpen = summary.highestOpenLine ?? 10;
              const locked = b.line > hOpen;
              return (
                <LineChip
                  key={b.line}
                  active={lineFilter === b.line}
                  locked={locked}
                  onClick={() => {
                    haptic.light();
                    setLineFilter(lineFilter === b.line ? null : b.line);
                  }}
                  label={`L${b.line}`}
                  count={b.partners}
                  sub={`${fmtUSDT(b.totalStaked, lang)} · +${fmtUSDT(b.income, lang)}`}
                />
              );
            })}
          </div>
        </GlassSurface>
      </div>

      {/* ФИЛЬТРЫ + СОРТИРОВКА */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { haptic.light(); setActiveOnly((v) => !v); }}
            className="inline-flex items-center gap-1.5 rounded-full
                       px-2.5 py-1 transition active:scale-95"
            style={{
              background: activeOnly
                ? 'color-mix(in oklab, var(--status-positive) 18%, transparent)'
                : 'rgba(255,255,255,0.03)',
              color: activeOnly
                ? 'var(--status-positive)'
                : 'var(--text-muted)',
              border: `1px solid ${activeOnly
                ? 'color-mix(in oklab, var(--status-positive) 35%, transparent)'
                : 'var(--glass-stroke)'}`,
            }}
          >
            <Activity size={12} strokeWidth={2} />
            <span className="text-[10px] font-bold uppercase tracking-[0.08em]">
              {activeOnly ? t('team.filterActiveOnly') : t('team.filterAll')}
            </span>
          </button>

          <div className="ml-auto flex items-center gap-1">
            <ArrowUpDown size={12} strokeWidth={1.8}
                         className="text-[var(--text-muted)]" />
            <SortBtn sortBy={sortBy} setSortBy={setSortBy}
                     value="turnover" label={t('team.sortTurnover')} />
            <SortBtn sortBy={sortBy} setSortBy={setSortBy}
                     value="slots" label={t('team.sortSlots')} />
            <SortBtn sortBy={sortBy} setSortBy={setSortBy}
                     value="income" label={t('team.sortIncome')} />
          </div>
        </div>
      </div>

      {/* Счётчик «N из M» */}
      {filteredTotal > 0 && (
        <div className="px-4 pt-3 text-[10px] text-[var(--text-muted)]
                        font-mono text-center">
          {t('team.shownOf', { shown: partners.length, total: filteredTotal })}
        </div>
      )}

      {/* СПИСОК ПАРТНЁРОВ */}
      <div className="px-4 pt-3 space-y-2">
        {partnersLoading && partners.length === 0 ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2
                            border-gray-600 border-t-indigo-400" />
          </div>
        ) : partners.length === 0 ? (
          <GlassSurface className="p-8 text-center">
            <Filter size={24} strokeWidth={1.5}
                    className="mx-auto mb-2 text-[var(--text-muted)]" />
            <p className="text-[13px] text-[var(--text-secondary)]">
              {t('team.nothingMatches')}
            </p>
          </GlassSurface>
        ) : (
          <AnimatePresence initial={false}>
            {partners.map((p, idx) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx, 8) * 0.02 }}
              >
                <PartnerCard p={p} />
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {/* Кнопка «Показать ещё» — подгружает +50 */}
        {hasMore && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="w-full rounded-xl py-2.5 text-[11px] font-mono
                       uppercase tracking-[0.14em] transition active:scale-[0.98]
                       disabled:opacity-50"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
              color: 'var(--text-secondary)',
            }}
          >
            {loadingMore
              ? t('team.loading')
              : t('team.showMore', { count: Math.min(PAGE_SIZE, filteredTotal - partners.length) })}
          </button>
        )}

        {!hasMore && partners.length > PAGE_SIZE && (
          <p className="text-[10px] text-[var(--text-muted)] text-center
                        mt-2 font-mono uppercase tracking-[0.14em]">
            {t('team.allPartners')}
          </p>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────
function Stat({
  label, value, hint, tone = 'neutral',
}: {
  label: string; value: string; hint?: string;
  tone?: 'neutral' | 'positive';
}) {
  const color = tone === 'positive'
    ? 'var(--status-positive)'
    : 'var(--text-primary)';
  return (
    <div className="rounded-xl p-3"
         style={{
           background: 'rgba(255,255,255,0.02)',
           border: '1px solid var(--glass-stroke)',
         }}>
      <div className="text-[9px] uppercase tracking-wider
                      text-[var(--text-muted)]">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-bold leading-none"
           style={{ color }}>
        {value}
      </div>
      {hint && (
        <div className="mt-1 text-[10px] text-[var(--text-muted)]">{hint}</div>
      )}
    </div>
  );
}

function LineChip({
  active, onClick, label, count, sub, locked = false,
}: {
  active: boolean; onClick: () => void;
  label: string; count: number; sub?: string;
  /** Линия закрыта у root'а — даём визуальный замочек, но клик
   *  разрешаем (чел всё равно видит оборот/людей). */
  locked?: boolean;
}) {
  const mutedColor = locked
    ? 'color-mix(in oklab, var(--text-muted) 65%, transparent)'
    : 'var(--text-secondary)';
  return (
    <button onClick={onClick}
      className="inline-flex flex-col items-start rounded-xl px-2.5 py-1.5
                 transition active:scale-95"
      style={{
        background: active
          ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
          : locked
            ? 'rgba(255,255,255,0.015)'
            : 'rgba(255,255,255,0.03)',
        color: active
          ? 'var(--accent-primary)'
          : mutedColor,
        border: `1px solid ${active
          ? 'color-mix(in oklab, var(--accent-primary) 35%, transparent)'
          : locked
            ? 'color-mix(in oklab, var(--text-muted) 22%, transparent)'
            : 'var(--glass-stroke)'}`,
        opacity: locked && !active ? 0.78 : 1,
      }}
    >
      <span className="flex items-center gap-1 text-[10px] font-bold
                       uppercase tracking-[0.08em]">
        {locked && (
          <Lock size={10} strokeWidth={2.2}
                style={{ color: 'color-mix(in oklab, var(--text-muted) 80%, transparent)' }} />
        )}
        {label}
        <span className="rounded-full px-1 text-[9px]"
              style={{ background: 'rgba(255,255,255,0.06)' }}>
          {count}
        </span>
      </span>
      {sub && (
        <span className="mt-0.5 text-[9px] text-[var(--text-muted)]">
          {sub}
        </span>
      )}
    </button>
  );
}

function SortBtn({
  sortBy, setSortBy, value, label,
}: {
  sortBy: string;
  setSortBy: (v: 'turnover' | 'slots' | 'income') => void;
  value: 'turnover' | 'slots' | 'income';
  label: string;
}) {
  const active = sortBy === value;
  return (
    <button onClick={() => setSortBy(value)}
      className="rounded-full px-2 py-1 text-[10px] font-bold uppercase
                 tracking-[0.08em] transition active:scale-95"
      style={{
        background: active
          ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
          : 'rgba(255,255,255,0.03)',
        color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
        border: `1px solid ${active
          ? 'color-mix(in oklab, var(--accent-primary) 35%, transparent)'
          : 'var(--glass-stroke)'}`,
      }}
    >
      {label}
    </button>
  );
}

function PartnerCard({ p }: { p: TeamPartner }) {
  const { t, i18n } = useTranslation();
  const lang: 'ru' | 'en' = i18n.language === 'ru' ? 'ru' : 'en';
  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  const [open, setOpen] = useState(false);
  // Фото всегда через backend-прокси (он сам освежит протухший TG URL).
  const photo = p.telegramId ? `/api/luna/user/photo/${p.telegramId}` : p.photoUrl;
  return (
    <GlassSurface className="overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left
                   transition active:scale-[0.995]"
      >
        {/* Avatar */}
        <div
          className="relative h-11 w-11 rounded-full flex-shrink-0
                     flex items-center justify-center"
          style={{
            background: photo
              ? `url(${photo}) center/cover`
              : `linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))`,
            border: '1.5px solid var(--glass-stroke-strong)',
          }}
        >
          {!photo && (
            <span className="text-[13px] font-bold text-white">
              {displayName(p).charAt(p.username ? 1 : 0).toUpperCase()}
            </span>
          )}
          {/* Level badge */}
          <span
            className="absolute -bottom-1 -right-1 rounded-full px-1
                       text-[8px] font-mono font-bold
                       text-[var(--text-primary)]"
            style={{
              background: 'var(--bg-base)',
              border: '1px solid var(--glass-stroke-strong)',
              minWidth: '18px',
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            L{p.line}
          </span>
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-semibold
                             text-[var(--text-primary)] truncate">
              {displayName(p)}
            </span>
            {p.currentRank !== null && p.currentRank > 0 && (
              <Crown size={10} strokeWidth={2}
                     className="text-[var(--status-warning)] flex-shrink-0" />
            )}
            {p.isActive ? (
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      background: 'var(--status-positive)',
                      boxShadow: '0 0 6px var(--status-positive)',
                    }} />
            ) : (
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0
                               bg-[var(--text-disabled)]" />
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[10px]
                          text-[var(--text-muted)]">
            <span>
              {t('team.slotsLabel')}: <b className="text-[var(--text-secondary)]">
                {p.activeSlots}
              </b>
            </span>
            <span>·</span>
            <span>
              {t('team.teamLabel')}: <b className="text-[var(--text-secondary)]">
                {p.teamSize}
              </b>
            </span>
          </div>
        </div>

        {/* Right stats */}
        <div className="text-right flex-shrink-0">
          <div className="text-[12px] font-mono font-bold
                          text-[var(--text-primary)]">
            {fmtUSDT(p.totalStaked, lang)}
          </div>
          <div className="text-[9px] text-[var(--text-muted)]">
            {t('team.turnoverLabel', { amount: fmtUSDT(p.teamTurnover, lang) })}
          </div>
        </div>

        <ChevronDown
          size={14}
          strokeWidth={1.8}
          className="text-[var(--text-muted)] flex-shrink-0 transition"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 pb-4
                            pt-0 border-t border-[var(--glass-stroke)]">
              <Detail label={t('team.detailMyIncome')}
                      value={`+${fmtUSDT(p.incomeFromUser, lang)} USD₮`}
                      tone="positive" />
              <Detail label={t('team.detailTeamTurnover')}
                      value={`${fmtUSDT(p.teamTurnover, lang)} USD₮`} />
              <Detail label={t('team.detailActiveSlots')}
                      value={String(p.activeSlots)} />
              <Detail label={t('team.detailPCActive')}
                      value={String(p.pcActive)} />
              <Detail label={t('team.detailTotalEarned')}
                      value={`${fmtUSDT(p.totalEarnedEver, lang)} USD₮`} />
              <Detail label={t('team.detailPartners')}
                      value={String(p.teamSize)} />
              <Detail label={t('team.detailLine')}
                      value={`L${p.line}`} />
              <Detail label={t('team.detailJoined')}
                      value={new Date(p.joinedAt).toLocaleDateString(locale)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassSurface>
  );
}

function Detail({
  label, value, tone = 'neutral',
}: {
  label: string; value: string; tone?: 'neutral' | 'positive';
}) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider
                      text-[var(--text-muted)]">
        {label}
      </div>
      <div className="mt-0.5 text-[12px] font-mono font-semibold"
           style={{
             color: tone === 'positive'
               ? 'var(--status-positive)'
               : 'var(--text-primary)',
           }}>
        {value}
      </div>
    </div>
  );
}

function SparkBars({
  days, values, max,
}: { days: string[]; values: number[]; max: number }) {
  // h-24 = 96px; у каждого бара собственная flex-child высота в процентах
  // от родителя. `items-end` прижимает их вниз.
  return (
    <div className="relative h-24 flex items-end gap-[2px] w-full">
      {values.map((v, i) => {
        const h = (v / max) * 100;
        const hasValue = v > 0;
        return (
          <div
            key={days[i]}
            className="flex-1 rounded-[2px] transition-all"
            title={`${days[i]}: ${v.toFixed(3)} USD₮`}
            style={{
              minWidth: '3px',
              height: `${Math.max(h, hasValue ? 6 : 2)}%`,
              background: hasValue
                ? 'linear-gradient(180deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)'
                : 'rgba(255,255,255,0.05)',
              boxShadow: hasValue
                ? '0 0 10px -2px var(--accent-primary)'
                : 'none',
            }}
          />
        );
      })}
    </div>
  );
}
